import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { FournisseurSession } from './composants/session.tsx';
import { MiseEnPage } from './composants/mise-en-page.tsx';
import { EspaceOnglets, ONGLETS_RESULTATS, ONGLETS_STOCK } from './composants/onglets.tsx';
import { Connexion } from './pages/Connexion.tsx';
import { TableauDeBord } from './pages/tableau-de-bord/index.tsx';
import { Ventes, VenteDetail } from './pages/ventes/index.tsx';
import { NouvelleVente } from './pages/ventes/NouvelleVente.tsx';
import { Facturation } from './pages/ventes/Facturation.tsx';
import { Clients } from './pages/clients/index.tsx';
import { Produits } from './pages/produits/index.tsx';
import { NouveauProduit } from './pages/produits/NouveauProduit.tsx';
import { Categories } from './pages/Categories.tsx';
import { Unites } from './pages/Unites.tsx';
import { Inventaires } from './pages/Inventaires.tsx';
import { Resultats, ResultatsDetaille } from './pages/resultats/index.tsx';
import { Stock } from './pages/stock/Stock.tsx';
import { Receptions } from './pages/stock/Receptions.tsx';
import { Transferts } from './pages/stock/Transferts.tsx';
import { Retours } from './pages/retours/index.tsx';
import { RetourDetailVue } from './pages/retours/detail.tsx';
import { Fournisseurs } from './pages/fournisseurs/index.tsx';
import { Anomalies } from './pages/Anomalies.tsx';
import { Journal } from './pages/Journal.tsx';
import { Utilisateurs } from './pages/utilisateurs/index.tsx';
import { Boutiques } from './pages/Boutiques.tsx';
// Les polices AVANT tout le reste : les `@font-face` doivent être déclarées
// avant que la moindre règle ne réclame ces familles.
import './polices/polices.css';
// Les tokens ENSUITE : ils sont la SEULE source des variables CSS, alias
// hérités compris. Aucune autre feuille ne déclare sur `:root`, donc l'ordre
// des deux imports suivants n'a plus d'incidence sur les couleurs.
import './theme/tokens.css';
import './theme/composants.css';
import './styles/global.css';

/**
 * Point d'entrée de l'administration.
 *
 * `retry: false` sur les requêtes : une erreur d'API porte un code métier
 * (RG-057, RG-162…) et rejouer ne changerait rien. Réessayer masquerait le
 * message, et ferait croire à une lenteur là où il y a un refus.
 */
const client = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: { retry: false },
  },
});

const racine = document.getElementById('racine');
if (racine === null) {
  throw new Error('Élément #racine introuvable.');
}

createRoot(racine).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <BrowserRouter>
        <FournisseurSession>
          <Routes>
            <Route path="/connexion" element={<Connexion />} />
            <Route element={<MiseEnPage />}>
              <Route index element={<TableauDeBord />} />
              <Route path="ventes" element={<Ventes />} />
              <Route path="ventes/nouvelle" element={<NouvelleVente />} />
              <Route path="ventes/:id" element={<VenteDetail />} />
              <Route path="facturation" element={<Facturation />} />
              <Route path="produits" element={<Produits />} />
              <Route path="produits/nouveau" element={<NouveauProduit />} />
              <Route path="clients" element={<Clients />} />
              <Route path="categories" element={<Categories />} />
              <Route path="unites" element={<Unites />} />
              {/*
                Les deux vues des résultats partagent une barre d'onglets,
                comme le stock ci-dessous : même mécanisme, même style.
              */}
              <Route
                element={
                  <EspaceOnglets titre="Vues des résultats" onglets={ONGLETS_RESULTATS} />
                }
              >
                <Route path="resultats" element={<Resultats />} />
                <Route path="resultats/detaille" element={<ResultatsDetaille />} />
              </Route>
              {/*
                Les quatre vues du stock partagent une barre d'onglets.

                La route enveloppante est SANS CHEMIN : elle n'ajoute aucun
                préfixe, donc `/receptions` reste `/receptions`. Regrouper
                l'interface ne doit pas casser des adresses en circulation.
              */}
              <Route
                element={
                  <EspaceOnglets titre="Vues du stock" onglets={ONGLETS_STOCK} />
                }
              >
                <Route path="stock" element={<Stock />} />
                <Route path="receptions" element={<Receptions />} />
                <Route path="transferts" element={<Transferts />} />
                <Route path="inventaires" element={<Inventaires />} />
              </Route>
              <Route path="fournisseurs" element={<Fournisseurs />} />
              <Route path="retours" element={<Retours />} />
              <Route path="retours/:id" element={<RetourDetailVue />} />
              <Route path="anomalies" element={<Anomalies />} />
              <Route path="journal" element={<Journal />} />
              <Route path="utilisateurs" element={<Utilisateurs />} />
              <Route path="boutiques" element={<Boutiques />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </FournisseurSession>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
